import { inject } from '@angular/core';
import { CanActivateFn } from '@angular/router';

import { AuthService } from './auth.service';

/**
 * SSO-only guard. If the user is not authenticated, redirect them to the IdP
 * with the requested URL preserved as the post-auth return target. The
 * redirect leaves the SPA via `window.location.assign`, so we return `false`
 * to prevent the route from activating during the brief navigation.
 *
 * There is no internal `/login` page — see `app.routes.ts` for the rationale.
 *
 * **Expired sessions (ADR-0073, KPM2-530).** `isAuthenticated()` only asks
 * whether a persisted `AuthState` exists; it says nothing about whether the
 * access token inside it is still good. A session whose token has expired but
 * which can still be refreshed is allowed through — `authInterceptor` swaps
 * the token on the first outbound call, invisibly. A session that has expired
 * and *cannot* be refreshed is sent straight back to the IdP: letting it
 * activate is what produced the KPM2-530 symptom, a fully-rendered shell
 * behind which every request fails forever. The check matters most for routes
 * that issue no API calls of their own, where the interceptor gate would
 * never fire and nothing else would notice the session was over.
 */
export const authGuard: CanActivateFn = (_route, state) => {
  const auth = inject(AuthService);

  if (!auth.isAuthenticated()) {
    auth.redirectToAuthProvider(state.url);
    return false;
  }

  if (auth.isTokenExpired() && !auth.canRefresh()) {
    // `state.url`, not the router's: mid-guard `router.url` still reports the
    // route being navigated away from, so letting the service infer it would
    // return the user to the wrong page.
    auth.forceReauthentication(state.url);
    return false;
  }

  return true;
};
